import {
  ICompanyPlanTaxByMcc,
  ICompanyPlanTaxCreditMethod,
  ICompanyPlanTaxCreditInstallment,
} from "@shared/types";
import { CompanyPlanUploadRowProps } from "./modal-company-plan-upload.type";

type MethodKey = "pix" | "debit" | "bankSlip";

export const parseNumber = (
  value: string | number | undefined | null,
): number | undefined => {
  if (value === undefined || value === null || value === "") return undefined;
  if (typeof value === "number") return value;

  const cleaned = String(value)
    .replace(/[^\d.,-]/g, "")
    .replace(",", ".");

  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? undefined : parsed;
};

export const formatBytes = (bytes: number, decimals = 2): string => {
  if (!+bytes) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

export const parseRateType = (
  type: string | undefined,
): "PERCENTAGE" | "FIXED" => {
  const t = String(type || "")
    .toLowerCase()
    .trim();

  const fixedPossibilities = ["fixa", "fixed", "fixo"];
  if (fixedPossibilities.some((p) => t.includes(p))) return "FIXED";

  return "PERCENTAGE";
};

export const parseRateLimit = (
  limit: string | number | undefined | null,
): number | undefined => {
  return parseNumber(limit);
};

export const mapMethodType = (method: string): string => {
  const raw = String(method).toLowerCase().trim();

  if (["pix"].some((p) => raw.includes(p))) return "pix";
  if (["debit", "débito", "debito"].some((p) => raw.includes(p)))
    return "debit";
  if (["boleto", "bankslip", "bank slip"].some((p) => raw.includes(p)))
    return "bankSlip";
  if (["credit", "crédito", "credito"].some((p) => raw.includes(p)))
    return "creditCard";

  return raw;
};

export const mapCaptureType = (
  capture: string,
): "online" | "pos" | "tap" | "default" => {
  const raw = String(capture).toLowerCase().trim();

  if (["online", "on-line", "e-commerce"].some((p) => raw.includes(p)))
    return "online";
  if (
    ["pos", "maquininha", "físico", "fisico", "maquina"].some((p) =>
      raw.includes(p),
    )
  )
    return "pos";
  if (["tap", "tap to phone"].some((p) => raw.includes(p))) return "tap";

  return "default";
};

export const mapNetworkType = (network: string): string => {
  const raw = String(network).toLowerCase().trim();

  if (["master", "mastercard"].some((p) => raw.includes(p)))
    return "mastercard";
  if (["visa"].some((p) => raw.includes(p))) return "visa";
  if (["elo"].some((p) => raw.includes(p))) return "elo";
  if (["hiper", "hipercard"].some((p) => raw.includes(p))) return "hipercard";
  if (
    [
      "amex",
      "american",
      "american express",
      "american_express",
      "americanExpress",
    ].some((p) => raw.includes(p))
  )
    return "american_express";

  return "default";
};

const getDefaultCreditCardNetworks = (): ICompanyPlanTaxCreditMethod => ({
  elo: { sellerRates: Array(24).fill(0) },
  visa: { sellerRates: Array(24).fill(0) },
  default: { sellerRates: Array(24).fill(0) },
  hipercard: { sellerRates: Array(24).fill(0) },
  mastercard: { sellerRates: Array(24).fill(0) },
  american_express: { sellerRates: Array(24).fill(0) },
});

export const buildTaxConfigs = (
  flatData: CompanyPlanUploadRowProps[],
): Record<string, ICompanyPlanTaxByMcc> => {
  const taxConfigsByMcc: Record<string, ICompanyPlanTaxByMcc> = {};

  const expandedData: CompanyPlanUploadRowProps[] = [];

  flatData.forEach((row) => {
    expandedData.push(row);

    const isDefaultDay = String(row.defaultDay).toLowerCase() === "true";
    const isDefaultBrand = String(row.defaultBrand).toLowerCase() === "true";

    if (isDefaultDay) {
      expandedData.push({
        ...row,
        settlementDays: "default",
      });
    }

    if (isDefaultBrand) {
      const hasExplicitDefault = flatData.some(
        (r) =>
          String(r.mcc) === String(row.mcc) &&
          mapMethodType(r.method) === mapMethodType(row.method) &&
          String(r.settlementDays) === String(row.settlementDays) &&
          String(r.statement) === String(row.statement) &&
          mapNetworkType(r.network) === "default",
      );

      if (!hasExplicitDefault) {
        expandedData.push({
          ...row,
          network: "default",
        });

        if (isDefaultDay) {
          expandedData.push({
            ...row,
            settlementDays: "default",
            network: "default",
          });
        }
      }
    }
  });

  expandedData.forEach((row) => {
    const mcc = String(row.mcc || "default").trim();
    const rawMethod = mapMethodType(row.method || "");
    const settlementDays = String(row.settlementDays || "1");

    let value = parseNumber(row.percentualRate);
    const rateType = parseRateType(row.rateType);
    const valueLimit = parseRateLimit(row.rateLimit);

    if (value !== undefined && rateType === "PERCENTAGE") {
      value = parseFloat((value * 100).toFixed(2));
    }

    if (!taxConfigsByMcc[mcc]) {
      taxConfigsByMcc[mcc] = {
        pix: {},
        debit: {},
        bankSlip: {},
        creditCard: {},
      };
    }

    const mccConfig = taxConfigsByMcc[mcc];

    if (
      rawMethod === "pix" ||
      rawMethod === "debit" ||
      rawMethod === "bankSlip"
    ) {
      const methodItem = mccConfig[rawMethod as MethodKey];

      if (methodItem) {
        methodItem[settlementDays] = {
          value: value || 0,
          rateType: rawMethod === "pix" ? "PERCENTAGE" : rateType,
          ...(valueLimit !== undefined ? { valueLimit } : {}),
        };
      }
    }

    if (rawMethod === "creditCard") {
      const rawCapture = String(row.capture || "default");
      const capturesSplitted = rawCapture.split("/");
      const captures = capturesSplitted.map((capture: string) =>
        mapCaptureType(capture),
      );

      const network = mapNetworkType(
        row.network || "default",
      ) as keyof ICompanyPlanTaxCreditMethod;
      const statementIdx = Math.max(1, parseNumber(row.statement) || 1) - 1; // 0 a 23

      if (!mccConfig.creditCard) {
        mccConfig.creditCard = {};
      }

      if (!mccConfig.creditCard[settlementDays]) {
        mccConfig.creditCard[settlementDays] = {
          pos: getDefaultCreditCardNetworks(),
          tap: getDefaultCreditCardNetworks(),
          online: getDefaultCreditCardNetworks(),
        };
      }

      captures.forEach((capture) => {
        const captureKey = capture as keyof ICompanyPlanTaxCreditInstallment;
        const captureConfig = mccConfig.creditCard![settlementDays][captureKey];

        if (captureConfig && captureConfig[network]) {
          if (value !== undefined) {
            captureConfig[network]!.sellerRates[statementIdx] = value;
          }
        }
      });
    }
  });

  Object.keys(taxConfigsByMcc).forEach((mcc) => {
    const mccConfig = taxConfigsByMcc[mcc];
    if (mccConfig?.creditCard) {
      Object.keys(mccConfig.creditCard).forEach((settlementDays) => {
        const captures = ["pos", "tap", "online"] as const;
        captures.forEach((capture) => {
          const captureConfig = mccConfig.creditCard?.[settlementDays]?.[capture];
          if (captureConfig) {
            const networks = Object.keys(
              captureConfig,
            ) as (keyof ICompanyPlanTaxCreditMethod)[];

            networks.forEach((network) => {
              const brandData = captureConfig[network];
              if (brandData?.sellerRates) {
                const isAllZeroOrEmpty =
                  brandData.sellerRates.length === 0 ||
                  brandData.sellerRates.every((rate) => !rate || rate === 0);

                if (isAllZeroOrEmpty) {
                  delete captureConfig[network];
                }
              }
            });
          }
        });
      });
    }
  });

  return taxConfigsByMcc;
};
